<template>
  <EditorModal
    :title="modalTitle"
    :subtitle="modalSubtitle"
    :show-close-button="true"
    @on-close-modal="goBack"
  >
    <template #content>
      <div class="kpi-wizard-layout">
        <!-- LEFT : form steps -->
        <div class="kpi-wizard-form">
          <!-- Progress bar -->
          <div class="kpi-progress-bar">
            <div class="kpi-progress-track">
              <div
                class="kpi-progress-fill"
                :style="{ width: `${progressPercent}%` }"
              />
            </div>
            <div class="kpi-progress-label">
              {{ t('customStats.editor.stepIndicator', { current: currentStepNumber, total: visibleSteps.length }) }}
            </div>
          </div>

          <!-- Edit mode warning banner -->
          <SpmMessage
            v-if="isEdit"
            severity="info"
            :closable="false"
            class="mb-3"
          >
            <strong>{{ t('customStats.editor.editLockNotice') }}</strong>
            <div class="mt-1 text-sm">
              {{ t('customStats.editor.editLockExplanation') }}
            </div>
          </SpmMessage>

          <!-- STEP 1: Source -->
          <section v-if="currentStep === 'source'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-database mr-2" />
              {{ t('customStats.editor.step.source.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.source.help') }}
            </p>

            <!-- Templates : raccourci pour démarrer d'un cas concret -->
            <div v-if="!isEdit" class="kpi-templates-shortcut">
              <button
                type="button"
                class="kpi-templates-button"
                @click="openTemplatesModal"
              >
                <i class="far fa-bolt mr-2" />
                {{ t('customStats.templates.openButton') }}
              </button>
            </div>

            <div
              v-for="group in sourceGroups"
              :key="group.key"
              class="kpi-source-group"
            >
              <h3 class="kpi-source-group-title">{{ group.label }}</h3>
              <div class="kpi-card-grid">
                <button
                  v-for="card in group.cards"
                  :key="`${card.type}-${card.schemaName}`"
                  type="button"
                  class="kpi-card"
                  :class="{ 'kpi-card--selected': isSourceSelected(card), 'kpi-card--disabled': isEdit }"
                  :disabled="isEdit"
                  @click="selectSource(card)"
                >
                  <i :class="`${card.icon} kpi-card-icon`" />
                  <div class="kpi-card-label">{{ card.label }}</div>
                  <div class="kpi-card-sublabel">{{ card.sublabel }}</div>
                  <i v-if="isSourceSelected(card)" class="fas fa-circle-check kpi-card-checkmark" />
                </button>
              </div>
            </div>

            <!-- Custom data definitions when "Mes données" is highlighted -->
            <div v-if="customDataSources.length > 0" class="kpi-custom-sources mt-3">
              <div class="text-sm font-medium mb-2">
                {{ t('customStats.editor.step.source.customSchemas') }}
              </div>
              <div class="kpi-mini-cards">
                <button
                  v-for="src in customDataSources"
                  :key="src.schemaName"
                  type="button"
                  class="kpi-mini-card"
                  :class="{ 'kpi-mini-card--selected': isSourceSelected(src), 'kpi-mini-card--disabled': isEdit }"
                  :disabled="isEdit"
                  @click="selectSource(src)"
                >
                  <i class="far fa-table kpi-mini-icon" />
                  {{ src.label }}
                </button>
              </div>
            </div>
          </section>

          <!-- STEP 2: Enrich (relations) -->
          <section v-else-if="currentStep === 'relations'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-link mr-2" />
              {{ t('customStats.editor.step.relations.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.relations.help') }}
            </p>

            <div v-if="availableRelations.length === 0" class="kpi-empty-hint">
              <i class="far fa-circle-info mr-2" />
              {{ t('customStats.editor.step.relations.none') }}
            </div>
            <div v-else class="kpi-card-grid">
              <button
                v-for="rel in availableRelations"
                :key="rel.name"
                type="button"
                class="kpi-card kpi-card--toggle"
                :class="{ 'kpi-card--selected': isRelationSelected(rel.name), 'kpi-card--disabled': isEdit }"
                :disabled="isEdit"
                @click="toggleRelation(rel.name)"
              >
                <i :class="`${rel.icon} kpi-card-icon`" />
                <div class="kpi-card-label">{{ rel.label }}</div>
                <div class="kpi-card-sublabel">{{ rel.sublabel }}</div>
                <span
                  v-if="rel.cardinality === 'many'"
                  class="kpi-relation-badge"
                  :title="t('customStats.editor.step.relations.filterOnlyHelp')"
                >
                  {{ t('customStats.editor.step.relations.filterOnlyBadge') }}
                </span>
                <i v-if="isRelationSelected(rel.name)" class="fas fa-circle-check kpi-card-checkmark" />
              </button>
            </div>
            <div
              v-if="hasOneToManyRelationSelected"
              class="kpi-info-banner"
            >
              <i class="far fa-circle-info mr-2" />
              {{ t('customStats.editor.step.relations.filterOnlyHelp') }}
            </div>
          </section>

          <!-- STEP 3: Measures (multi) -->
          <section v-else-if="currentStep === 'measures'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-calculator mr-2" />
              {{ t('customStats.editor.step.measures.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.measures.help') }}
              <span class="text-sm text-color-secondary">
                ({{ form.measures.length }}/{{ MAX_MEASURES_PER_KPI }})
              </span>
            </p>
            <div
              v-if="hasOneToManyRelationSelected"
              class="kpi-info-banner"
            >
              <i class="far fa-circle-info mr-2" />
              {{ t('customStats.editor.step.measures.filterOnlyNotice') }}
            </div>

            <div class="kpi-measures-list">
              <div
                v-for="(measure, mIdx) in form.measures"
                :key="mIdx"
                class="kpi-measure-card"
              >
                <div class="kpi-measure-header">
                  <span class="kpi-measure-index">#{{ mIdx + 1 }}</span>
                  <span class="kpi-measure-summary">{{
                    (measure.label?.fr || measure.label?.en || t('customStats.editor.step.measures.untitled'))
                  }}</span>
                  <Button
                    v-if="form.measures.length > 1"
                    icon="far fa-trash"
                    text
                    rounded
                    severity="danger"
                    :disabled="isEdit"
                    @click="removeMeasure(mIdx)"
                  />
                </div>

                <!-- Type d'agrégation : grille compacte -->
                <div class="kpi-measure-section">
                  <label class="kpi-field-label">{{ t('customStats.editor.step.measures.aggType') }}</label>
                  <div class="kpi-mini-cards">
                    <button
                      v-for="card in aggregationCardsForMeasure(measure)"
                      :key="card.value"
                      type="button"
                      class="kpi-mini-card"
                      :class="{ 'kpi-mini-card--selected': measure.type === card.value, 'kpi-mini-card--disabled': isEdit || !card.compatible }"
                      :disabled="isEdit || !card.compatible"
                      @click="setMeasureType(mIdx, card.value)"
                    >
                      <i :class="`${card.icon} kpi-mini-icon`" />
                      {{ card.label }}
                    </button>
                  </div>
                </div>

                <!-- Field (sauf pour count) -->
                <div v-if="measure.type !== 'count'" class="kpi-measure-section">
                  <label class="kpi-field-label">{{ t('customStats.editor.step.measures.field') }}</label>
                  <Dropdown
                    :model-value="measure.field"
                    :options="aggregationFieldOptionsForMeasure(measure)"
                    option-label="label"
                    option-value="path"
                    :placeholder="t('customStats.editor.placeholder.chooseField')"
                    :disabled="isEdit"
                    show-clear
                    filter
                    class="w-full max-w-30rem"
                    @update:model-value="(v) => setMeasureField(mIdx, v)"
                  >
                    <template #option="{ option }">
                      <div class="flex flex-column" :title="fieldHint(option)">
                        <span class="font-medium">{{ displayLabel(option) }}</span>
                        <small class="text-color-secondary">{{ typeLabel(option.type) }}</small>
                      </div>
                    </template>
                  </Dropdown>
                </div>

                <!-- Label par mesure (multilang) -->
                <div class="kpi-measure-section">
                  <label class="kpi-field-label">{{ t('customStats.editor.step.measures.label') }}</label>
                  <LocalizedTextInput
                    :id="`kpi-measure-label-${mIdx}`"
                    :model-value="measure.label ?? {}"
                    :default-languages="shopLangs"
                    type="text"
                    class="w-full max-w-30rem"
                    @update:model-value="setMeasureLabelObject(mIdx, $event)"
                  />
                </div>

                <!-- Format d'affichage + suffixe optionnel -->
                <div class="kpi-measure-section">
                  <label class="kpi-field-label">{{ t('customStats.editor.step.measures.format') }}</label>
                  <div class="flex gap-2 align-items-center flex-wrap">
                    <Dropdown
                      :model-value="measure.format ?? 'number'"
                      :options="measureFormatOptions"
                      option-label="label"
                      option-value="value"
                      class="w-15rem"
                      @update:model-value="(v) => setMeasureFormat(mIdx, v)"
                    />
                    <InputText
                      :model-value="measure.suffix ?? ''"
                      :placeholder="t('customStats.editor.step.measures.suffixPlaceholder')"
                      :maxlength="8"
                      class="w-10rem"
                      @update:model-value="(v) => setMeasureSuffix(mIdx, String(v ?? ''))"
                    />
                    <small class="text-color-secondary">
                      {{ t('customStats.editor.step.measures.formatHint') }}
                    </small>
                  </div>
                </div>

              </div>

              <Button
                v-if="form.measures.length < MAX_MEASURES_PER_KPI"
                :label="t('customStats.editor.step.measures.add')"
                icon="far fa-plus"
                outlined
                :disabled="isEdit"
                @click="addMeasure"
              />
            </div>
          </section>

          <!-- STEP 4: Dimensions disponibles -->
          <section v-else-if="currentStep === 'dimensions'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-layer-group mr-2" />
              {{ t('customStats.editor.step.dimensions.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.dimensions.help') }}
              <span class="text-sm text-color-secondary">
                ({{ form.dimensions?.length ?? 0 }}/{{ MAX_DIMENSIONS_PER_KPI }})
              </span>
            </p>
            <div
              v-if="hasOneToManyRelationSelected"
              class="kpi-info-banner"
            >
              <i class="far fa-circle-info mr-2" />
              {{ t('customStats.editor.step.dimensions.filterOnlyNotice') }}
            </div>

            <div class="kpi-measures-list">
              <div
                v-for="(dim, dIdx) in form.dimensions"
                :key="dIdx"
                class="kpi-measure-card"
              >
                <div class="kpi-measure-header">
                  <span class="kpi-measure-index">#{{ dIdx + 1 }}</span>
                  <span class="kpi-measure-summary">{{
                    (dim.label?.fr || dim.label?.en || t('customStats.editor.step.dimensions.untitled'))
                  }}</span>
                  <Button
                    icon="far fa-trash"
                    text
                    rounded
                    severity="danger"
                    :disabled="isEdit"
                    @click="removeDimension(dIdx)"
                  />
                </div>

                <div class="kpi-measure-section">
                  <label class="kpi-field-label">{{ t('customStats.editor.step.dimensions.field') }}</label>
                  <Dropdown
                    :model-value="dim.field"
                    :options="dimensionFieldOptions"
                    option-label="label"
                    option-value="path"
                    :placeholder="t('customStats.editor.placeholder.chooseField')"
                    :disabled="isEdit"
                    filter
                    class="w-full max-w-30rem"
                    @update:model-value="(v) => setDimensionField(dIdx, String(v ?? ''))"
                  >
                    <template #option="{ option }">
                      <div class="flex flex-column" :title="fieldHint(option)">
                        <span class="font-medium">{{ displayLabel(option) }}</span>
                        <small class="text-color-secondary">{{ typeLabel(option.type) }}</small>
                      </div>
                    </template>
                  </Dropdown>
                </div>

                <div class="kpi-measure-section">
                  <label class="kpi-field-label">{{ t('customStats.editor.step.dimensions.label') }}</label>
                  <LocalizedTextInput
                    :id="`kpi-dimension-label-${dIdx}`"
                    :model-value="dim.label ?? {}"
                    :default-languages="shopLangs"
                    type="text"
                    class="w-full max-w-30rem"
                    @update:model-value="setDimensionLabelObject(dIdx, $event)"
                  />
                </div>
              </div>

              <Button
                v-if="(form.dimensions?.length ?? 0) < MAX_DIMENSIONS_PER_KPI"
                :label="t('customStats.editor.step.dimensions.add')"
                icon="far fa-plus"
                outlined
                :disabled="isEdit"
                @click="addDimension"
              />
            </div>
          </section>

          <!-- STEP 6: Time field -->
          <section v-else-if="currentStep === 'time'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-clock mr-2" />
              {{ t('customStats.editor.step.time.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.time.help') }}
            </p>

            <div class="kpi-toggle-group mb-3">
              <button
                type="button"
                class="kpi-toggle-btn"
                :class="{ 'kpi-toggle-btn--active': !timeEnabled }"
                :disabled="isEdit"
                @click="setTime(false)"
              >
                {{ t('customStats.editor.step.time.no') }}
              </button>
              <button
                type="button"
                class="kpi-toggle-btn"
                :class="{ 'kpi-toggle-btn--active': timeEnabled }"
                :disabled="isEdit || timeFieldOptions.length === 0"
                @click="setTime(true)"
              >
                {{ t('customStats.editor.step.time.yes') }}
              </button>
            </div>

            <div v-if="timeEnabled">
              <label class="kpi-field-label">{{ t('customStats.editor.step.time.field') }}</label>
              <Dropdown
                v-model="form.time_field"
                :options="timeFieldOptions"
                option-label="label"
                option-value="path"
                :placeholder="t('customStats.editor.placeholder.chooseField')"
                :disabled="isEdit"
                class="w-full max-w-30rem"
              >
                <template #option="{ option }">
                  <div class="flex flex-column" :title="fieldHint(option)">
                    <span class="font-medium">{{ displayLabel(option) }}</span>
                    <small class="text-color-secondary">{{ typeLabel(option.type) }}</small>
                  </div>
                </template>
              </Dropdown>
            </div>
            <SpmMessage v-else-if="timeFieldOptions.length === 0" severity="warn" :closable="false">
              {{ t('customStats.editor.step.time.noTimeFieldAvailable') }}
            </SpmMessage>
          </section>

          <!-- STEP 7: Filters -->
          <section v-else-if="currentStep === 'filters'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-filter mr-2" />
              {{ t('customStats.editor.step.filters.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.filters.help') }}
              <span class="text-sm text-color-secondary">
                ({{ form.filters?.length ?? 0 }}/{{ MAX_FILTERS }})
              </span>
            </p>

            <div class="space-y-2">
              <div
                v-for="(filter, idx) in form.filters"
                :key="idx"
                class="kpi-filter-row"
              >
                <Dropdown
                  v-model="filter.field"
                  :options="availableFields"
                  option-label="label"
                  option-value="path"
                  :placeholder="t('customStats.editor.placeholder.field')"
                  filter
                  class="kpi-filter-field"
                />
                <Dropdown
                  v-model="filter.operator"
                  :options="filterOperatorOptions"
                  option-label="label"
                  option-value="value"
                  :placeholder="t('customStats.editor.placeholder.operator')"
                  class="kpi-filter-op"
                />
                <InputText
                  v-if="filterNeedsValue(filter.operator)"
                  :model-value="filterValueProxy[idx]"
                  :placeholder="t('customStats.editor.placeholder.value')"
                  class="kpi-filter-value"
                  @update:model-value="(v) => onFilterValueChange(idx, v)"
                />
                <Button
                  icon="far fa-trash"
                  text
                  rounded
                  severity="danger"
                  @click="removeFilter(idx)"
                />
              </div>
              <Button
                v-if="(form.filters?.length ?? 0) < MAX_FILTERS"
                :label="t('customStats.editor.action.addFilter')"
                icon="far fa-plus"
                outlined
                size="small"
                @click="addFilter"
              />
            </div>
          </section>

          <!-- STEP 8: Display & naming -->
          <section v-else-if="currentStep === 'display'" class="kpi-step">
            <h2 class="kpi-step-title">
              <i class="far fa-paintbrush mr-2" />
              {{ t('customStats.editor.step.display.title') }}
            </h2>
            <p class="kpi-step-help">
              {{ t('customStats.editor.step.display.help') }}
            </p>

            <!-- Name multilang -->
            <div class="mb-3">
              <label class="kpi-field-label">{{ t('customStats.editor.step.display.name') }}</label>
              <div v-for="lang in shopLangs" :key="lang" class="kpi-lang-row">
                <SpmBadge :label="lang.toUpperCase()" size="sm" :colors="{ bg: '#f3f4f6', color: '#374151' }" />
                <InputText
                  v-model="nameByLang[lang]"
                  :placeholder="t('customStats.editor.placeholder.name')"
                  class="flex-1"
                />
              </div>
            </div>

            <!-- Description multilang -->
            <div class="mb-3">
              <label class="kpi-field-label">{{ t('customStats.editor.step.display.description') }}</label>
              <div v-for="lang in shopLangs" :key="`desc-${lang}`" class="kpi-lang-row">
                <SpmBadge :label="lang.toUpperCase()" size="sm" :colors="{ bg: '#f3f4f6', color: '#374151' }" />
                <Textarea
                  v-model="descByLang[lang]"
                  :placeholder="t('customStats.editor.placeholder.description')"
                  :rows="2"
                  class="flex-1"
                />
              </div>
            </div>

            <!-- Lookback -->
            <div v-if="!isEdit" class="mb-3">
              <label class="kpi-field-label">
                {{ t('customStats.editor.step.display.lookback') }}
                <span class="text-sm text-color-secondary ml-1">
                  ({{ t('customStats.editor.step.display.lookbackHelp', { max: MAX_LOOKBACK_MONTHS }) }})
                </span>
              </label>
              <InputNumber
                v-model="form.lookback_max_months"
                :min="0"
                :max="MAX_LOOKBACK_MONTHS"
                show-buttons
                class="w-8rem"
              />
            </div>
          </section>

          <!-- Step navigation -->
          <div class="kpi-step-nav">
            <SpmButton
              v-if="canGoBack"
              :label="t('customStats.editor.action.back')"
              icon="far fa-arrow-left"
              class-style="p-button p-button-secondary p-button-outlined"
              @click="goBackStep"
            />
            <span v-else />

            <SpmButton
              v-if="!isLastStep"
              :label="t('customStats.editor.action.next')"
              icon="far fa-arrow-right"
              icon-pos="right"
              class-style="p-button p-button-primary"
              :disabled="!canGoNext"
              @click="goNextStep"
            />
            <SpmButton
              v-else
              :label="isEdit ? t('common.save') : t('customStats.editor.action.create')"
              icon="far fa-check"
              class-style="p-button p-button-primary"
              :disabled="!canSave || isSaving"
              :loading="isSaving"
              @click="onSave"
            />
          </div>
        </div>

        <!-- RIGHT : live preview -->
        <div class="kpi-wizard-preview">
          <div class="kpi-preview-card">
            <div class="kpi-preview-header">
              <i class="far fa-lightbulb mr-2" />
              {{ t('customStats.editor.preview.summary') }}
            </div>

            <div class="kpi-preview-body">
              <div v-if="!form.source_schema_name" class="kpi-preview-placeholder">
                <i class="far fa-arrow-left mr-2" />
                {{ t('customStats.editor.preview.startBuilding') }}
              </div>

              <div v-else class="kpi-preview-summary">
                <div class="kpi-preview-line">
                  <i class="far fa-database kpi-preview-line-icon" />
                  <span>{{ summarySource }}</span>
                </div>
                <div v-if="form.relations_chain && form.relations_chain.length" class="kpi-preview-line">
                  <i class="far fa-link kpi-preview-line-icon" />
                  <span>{{ summaryRelations }}</span>
                </div>
                <div
                  v-for="(line, mIdx) in summaryMeasures"
                  :key="`measure-${mIdx}`"
                  class="kpi-preview-line"
                >
                  <i class="far fa-calculator kpi-preview-line-icon" />
                  <span>{{ line }}</span>
                </div>
                <div v-if="(form.dimensions?.length ?? 0) > 0" class="kpi-preview-line">
                  <i class="far fa-layer-group kpi-preview-line-icon" />
                  <span>{{ summaryDimensions }}</span>
                </div>
                <div v-if="form.time_field" class="kpi-preview-line">
                  <i class="far fa-clock kpi-preview-line-icon" />
                  <span>{{ summaryTime }}</span>
                </div>
                <div v-if="form.filters && form.filters.length" class="kpi-preview-line">
                  <i class="far fa-filter kpi-preview-line-icon" />
                  <span>{{ summaryFilters }}</span>
                </div>
              </div>
            </div>

            <div v-if="canRunPreview" class="kpi-preview-actions">
              <SpmButton
                :label="t('customStats.editor.action.preview')"
                icon="far fa-play"
                class-style="p-button p-button-secondary p-button-sm"
                :loading="isPreviewLoading"
                @click="onPreview"
              />
            </div>

            <div v-if="previewResult" class="kpi-preview-result mt-3">
              <div class="kpi-preview-result-header">
                {{ t('customStats.editor.preview.title') }}
                <span class="text-sm text-color-secondary">
                  ({{ previewResult.rowCount }} {{ t('customStats.editor.preview.rows') }})
                </span>
              </div>

              <!-- Cost-aware metrics : ce que ce KPI va coûter en prod -->
              <div class="kpi-preview-cost">
                <div
                  v-if="previewResult.sourceRowsScanned !== null && previewResult.sourceRowsScanned !== undefined"
                  class="kpi-preview-cost-cell"
                >
                  <span class="kpi-preview-cost-label">{{ t('customStats.editor.preview.cost.scanned') }}</span>
                  <span class="kpi-preview-cost-value">
                    {{ formatNumber(previewResult.sourceRowsScanned) }}
                  </span>
                  <span class="kpi-preview-cost-sub">
                    {{ t('customStats.editor.preview.cost.scannedSub', { days: previewResult.previewDays }) }}
                  </span>
                </div>
                <div
                  v-if="previewResult.sourceRowsScanned !== null && previewResult.sourceRowsScanned !== undefined"
                  class="kpi-preview-cost-cell"
                >
                  <span class="kpi-preview-cost-label">{{ t('customStats.editor.preview.cost.yearly') }}</span>
                  <span class="kpi-preview-cost-value">
                    ~ {{ formatNumber(estimatedYearlyRows(previewResult)) }}
                  </span>
                  <span class="kpi-preview-cost-sub">
                    {{ t('customStats.editor.preview.cost.yearlySub') }}
                  </span>
                </div>
                <div
                  v-if="previewResult.durationMs !== undefined"
                  class="kpi-preview-cost-cell"
                >
                  <span class="kpi-preview-cost-label">{{ t('customStats.editor.preview.cost.duration') }}</span>
                  <span
                    class="kpi-preview-cost-value"
                    :class="{ 'kpi-preview-cost-value--warn': previewResult.durationMs > 1000 }"
                  >
                    {{ previewResult.durationMs }} ms
                  </span>
                  <span class="kpi-preview-cost-sub">
                    {{ t('customStats.editor.preview.cost.durationSub') }}
                  </span>
                </div>
              </div>
              <div v-if="previewResult.rows.length === 0" class="kpi-empty-hint mt-2">
                {{ t('customStats.editor.preview.noData') }}
              </div>
              <DataTable
                v-else
                :value="previewResult.rows.slice(0, 10)"
                class="kpi-preview-table mt-2"
                :show-gridlines="false"
                size="small"
              >
                <Column
                  v-for="(_, key) in previewResult.rows[0]"
                  :key="key"
                  :field="String(key)"
                  :header="String(key)"
                />
              </DataTable>
              <details class="mt-2">
                <summary class="cursor-pointer text-sm text-color-secondary">
                  {{ t('customStats.editor.preview.viewSql') }}
                </summary>
                <pre class="kpi-preview-sql">{{ previewResult.sql }}</pre>
              </details>
            </div>
          </div>
        </div>
      </div>

      <Toast position="top-right" />

      <!-- Templates picker modal -->
      <Dialog
        v-model:visible="templatesModalOpen"
        modal
        :header="t('customStats.templates.modalTitle')"
        :style="{ width: '900px', maxWidth: '95vw' }"
        :draggable="false"
      >
        <p class="kpi-templates-help">
          {{ t('customStats.templates.modalHelp') }}
        </p>
        <div
          v-for="group in templateGroups"
          :key="group.key"
          class="kpi-templates-group"
        >
          <h3 class="kpi-templates-group-title">{{ group.label }}</h3>
          <div class="kpi-templates-grid">
            <button
              v-for="tpl in group.templates"
              :key="tpl.id"
              type="button"
              class="kpi-template-card"
              @click="applyTemplate(tpl)"
            >
              <i :class="`${tpl.icon} kpi-template-icon`" />
              <div class="kpi-template-label">{{ t(tpl.labelKey) }}</div>
              <div class="kpi-template-desc">{{ t(tpl.descriptionKey) }}</div>
            </button>
          </div>
        </div>
      </Dialog>
    </template>
  </EditorModal>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, onMounted, reactive, watch,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import EditorModal from '@/components/modals/EditorModal.vue';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import SpmMessage from '@/components/SpmUI/SpmMessage.vue';
import SpmBadge from '@/components/table-rest-api/SpmBadge.vue';
import LocalizedTextInput from '@/components/fields/LocalizedTextInput.vue';
import Dropdown from 'primevue/dropdown';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Toast from 'primevue/toast';
import Dialog from 'primevue/dialog';
import { useToast } from 'primevue/usetoast';
import {
  fetchAvailableSources,
  fetchAvailableFields,
  createKpi,
  updateKpi,
  fetchKpi,
  previewKpi,
} from '@/composables/customStats/customKpi';
import {
  AGGREGATIONS_BY_FIELD_TYPE,
  ALL_AGGREGATION_TYPES,
  DEFAULT_LOOKBACK_MONTHS,
  DIMENSION_ELIGIBLE_TYPES,
  MAX_DIMENSIONS_PER_KPI,
  MAX_FILTERS,
  MAX_LOOKBACK_MONTHS,
  MAX_MEASURES_PER_KPI,
  TIME_FIELD_ELIGIBLE_TYPES,
} from '@/composables/customStats/customKpi.interfaces';
import type {
  AggregationType,
  AvailableField,
  AvailableSource,
  CreateCustomKpiPayload,
  CustomKpiDefinition,
  KpiDimension,
  KpiFilter,
  KpiMeasure,
  PreviewResult,
  SourceType,
} from '@/composables/customStats/customKpi.interfaces';
import {
  CUSTOM_KPI_TEMPLATES,
  CUSTOM_KPI_TEMPLATE_GROUP_ORDER,
} from '@/composables/customStats/customKpiTemplates';
import type { CustomKpiTemplate } from '@/composables/customStats/customKpiTemplates';

type StepKey =
  | 'source'
  | 'relations'
  | 'measures'
  | 'dimensions'
  | 'time'
  | 'filters'
  | 'display';

const SYSTEM_SOURCE_META: Record<string, { icon: string; labelKey: string; sublabelKey: string }> = {
  orders: { icon: 'far fa-cart-shopping', labelKey: 'customStats.source.system.orders', sublabelKey: 'customStats.source.system.orders_sub' },
  contacts: { icon: 'far fa-user', labelKey: 'customStats.source.system.contacts', sublabelKey: 'customStats.source.system.contacts_sub' },
  products: { icon: 'far fa-box-open', labelKey: 'customStats.source.system.products', sublabelKey: 'customStats.source.system.products_sub' },
  carts: { icon: 'far fa-basket-shopping', labelKey: 'customStats.source.system.carts', sublabelKey: 'customStats.source.system.carts_sub' },
  orders_products: { icon: 'far fa-cart-flatbed', labelKey: 'customStats.source.system.orders_products', sublabelKey: 'customStats.source.system.orders_products_sub' },
  carts_products: { icon: 'far fa-basket-shopping', labelKey: 'customStats.source.system.carts_products', sublabelKey: 'customStats.source.system.carts_products_sub' },
  contact_device: { icon: 'far fa-mobile-screen', labelKey: 'customStats.source.system.contact_device', sublabelKey: 'customStats.source.system.contact_device_sub' },
  manufacturers: { icon: 'far fa-tag', labelKey: 'customStats.source.system.manufacturers', sublabelKey: 'customStats.source.system.manufacturers_sub' },
  carriers: { icon: 'far fa-truck', labelKey: 'customStats.source.system.carriers', sublabelKey: 'customStats.source.system.carriers_sub' },
  categories: { icon: 'far fa-folder-tree', labelKey: 'customStats.source.system.categories', sublabelKey: 'customStats.source.system.categories_sub' },
  product_attributes: { icon: 'far fa-shapes', labelKey: 'customStats.source.system.product_attributes', sublabelKey: 'customStats.source.system.product_attributes_sub' },
  vouchers: { icon: 'far fa-ticket', labelKey: 'customStats.source.system.vouchers', sublabelKey: 'customStats.source.system.vouchers_sub' },
  customers_groups_list: { icon: 'far fa-users', labelKey: 'customStats.source.system.customers_groups_list', sublabelKey: 'customStats.source.system.customers_groups_list_sub' },
};

const AGGREGATION_META: Record<AggregationType, { icon: string; labelKey: string; sublabelKey: string }> = {
  count: { icon: 'far fa-hashtag', labelKey: 'customStats.aggCard.count.label', sublabelKey: 'customStats.aggCard.count.sublabel' },
  count_distinct: { icon: 'far fa-fingerprint', labelKey: 'customStats.aggCard.count_distinct.label', sublabelKey: 'customStats.aggCard.count_distinct.sublabel' },
  sum: { icon: 'far fa-sigma', labelKey: 'customStats.aggCard.sum.label', sublabelKey: 'customStats.aggCard.sum.sublabel' },
  avg: { icon: 'far fa-chart-mixed', labelKey: 'customStats.aggCard.avg.label', sublabelKey: 'customStats.aggCard.avg.sublabel' },
  min: { icon: 'far fa-arrow-down-to-line', labelKey: 'customStats.aggCard.min.label', sublabelKey: 'customStats.aggCard.min.sublabel' },
  max: { icon: 'far fa-arrow-up-to-line', labelKey: 'customStats.aggCard.max.label', sublabelKey: 'customStats.aggCard.max.sublabel' },
};

export default defineComponent({
  name: 'CustomKpiEditor',
  components: {
    EditorModal,
    SpmButton,
    SpmMessage,
    SpmBadge,
    LocalizedTextInput,
    Dropdown,
    InputText,
    InputNumber,
    Textarea,
    Button,
    DataTable,
    Column,
    Toast,
    Dialog,
  },
  setup() {
    const { t, locale } = useI18n();
    const router = useRouter();
    const route = useRoute();
    const toast = useToast();

    const editId = computed(() => (route.name === 'custom-kpis.edit' ? (route.params.id as string) : null));
    const duplicateId = computed(() => (route.query.duplicate as string | undefined));
    const isEdit = computed(() => !!editId.value);

    // ----- Form state -----
    const makeDefaultMeasure = (): KpiMeasure => ({
      type: 'count',
      field: undefined,
      format: 'number',
      suffix: null,
      label: { fr: '', en: '' },
    });

    const makeDefaultDimension = (): KpiDimension => ({
      field: '',
      label: { fr: '', en: '' },
    });

    const form = reactive<CreateCustomKpiPayload>({
      name: {},
      description: {},
      measures: [makeDefaultMeasure()],
      dimensions: [],
      source_type: 'custom',
      source_schema_name: '',
      relations_chain: [],
      time_field: '',
      time_field_explicit: true,
      filters: [],
      lookback_max_months: DEFAULT_LOOKBACK_MONTHS,
    });

    const availableSources = ref<AvailableSource[]>([]);
    const availableFields = ref<AvailableField[]>([]);
    const isLoadingFields = ref(false);
    const isSaving = ref(false);
    const isPreviewLoading = ref(false);
    const previewResult = ref<PreviewResult | null>(null);

    // Multi-langue : pour V1 fr+en. À étendre selon la conf shop.
    const shopLangs = ['fr', 'en'];
    const nameByLang = reactive<Record<string, string>>({ fr: '', en: '' });
    const descByLang = reactive<Record<string, string>>({ fr: '', en: '' });
    const filterValueProxy = reactive<Record<number, string>>({});

    // Toggles UI
    const timeEnabled = ref(false);

    // ----- Steps -----
    const stepKeys: StepKey[] = ['source', 'relations', 'measures', 'dimensions', 'time', 'filters', 'display'];
    const currentStepIndex = ref(0);

    const visibleSteps = computed<StepKey[]>(() => stepKeys);

    const currentStep = computed<StepKey>(() => visibleSteps.value[currentStepIndex.value] ?? 'source');
    const currentStepNumber = computed(() => currentStepIndex.value + 1);
    const isLastStep = computed(() => currentStepIndex.value === visibleSteps.value.length - 1);
    const canGoBack = computed(() => currentStepIndex.value > 0);
    const progressPercent = computed(() => {
      const total = visibleSteps.value.length;
      return total > 0 ? Math.round(((currentStepIndex.value + 1) / total) * 100) : 0;
    });

    // ----- Source cards -----
    const systemSourceCards = computed(() => availableSources.value
      .filter((s) => s.type === 'system')
      .map((s) => {
        const meta = SYSTEM_SOURCE_META[s.schemaName];
        return {
          ...s,
          icon: meta?.icon ?? 'far fa-database',
          label: meta ? t(meta.labelKey) : s.label,
          sublabel: meta ? t(meta.sublabelKey) : s.tableFqn,
        };
      }));

    const customDataSources = computed(() => availableSources.value
      .filter((s) => s.type === 'custom')
      .map((s) => ({
        ...s,
        icon: 'far fa-table',
        label: s.label,
        sublabel: s.tableFqn,
      })));

    // Mapping schema → groupe thématique pour l'affichage en step 1.
    // Tout schema absent du mapping va dans 'sales' par défaut (sécu).
    const SOURCE_GROUP_BY_SCHEMA: Record<string, 'sales' | 'catalog' | 'customers'> = {
      orders: 'sales',
      orders_products: 'sales',
      carts: 'sales',
      carts_products: 'sales',
      vouchers: 'sales',
      products: 'catalog',
      product_attributes: 'catalog',
      manufacturers: 'catalog',
      categories: 'catalog',
      carriers: 'catalog',
      contacts: 'customers',
      contact_device: 'customers',
      customers_groups_list: 'customers',
    };
    const SOURCE_GROUP_ORDER: Array<'sales' | 'catalog' | 'customers' | 'custom'> = [
      'sales',
      'catalog',
      'customers',
      'custom',
    ];

    const sourceGroups = computed(() => {
      const buckets: Record<string, ReturnType<typeof systemSourceCards.value.slice>> = {
        sales: [],
        catalog: [],
        customers: [],
        custom: [],
      };
      systemSourceCards.value.forEach((card) => {
        const groupKey = SOURCE_GROUP_BY_SCHEMA[card.schemaName] ?? 'sales';
        buckets[groupKey].push(card);
      });
      if (customDataSources.value.length > 0) {
        buckets.custom.push({
          type: 'custom' as SourceType,
          schemaName: '__custom_aggregator__',
          label: t('customStats.source.customMine'),
          sublabel: t('customStats.source.customMineSub', { count: customDataSources.value.length }),
          icon: 'far fa-folder-open',
          tableFqn: '',
        });
      }
      return SOURCE_GROUP_ORDER
        .filter((key) => buckets[key].length > 0)
        .map((key) => ({
          key,
          label: t(`customStats.source.groups.${key}`),
          cards: buckets[key],
        }));
    });

    const sourceCards = computed(() => {
      const cards = [...systemSourceCards.value];
      if (customDataSources.value.length > 0) {
        cards.push({
          type: 'custom' as SourceType,
          schemaName: '__custom_aggregator__',
          label: t('customStats.source.customMine'),
          sublabel: t('customStats.source.customMineSub', { count: customDataSources.value.length }),
          icon: 'far fa-folder-open',
          tableFqn: '',
        });
      }
      return cards;
    });

    // ----- Relations available on current source -----
    // On lit les relationships exposées par la source (système ou custom)
    // depuis l'objet AvailableSource déjà chargé. Le backend renvoie pour
    // chaque source les couples {propertyName, targetSchema, type}.
    const RELATION_ICONS: Record<string, string> = {
      products: 'far fa-box-open',
      product: 'far fa-box-open',
      contacts: 'far fa-user',
      contact: 'far fa-user',
      orders: 'far fa-cart-shopping',
      order: 'far fa-cart-shopping',
      carts: 'far fa-basket-shopping',
      cart: 'far fa-basket-shopping',
      manufacturer: 'far fa-tag',
      carrier: 'far fa-truck',
      categories: 'far fa-folder-tree',
      product_categories: 'far fa-folder-tree',
      main_category: 'far fa-folder',
    };

    const safeName = (propertyName: string | undefined, targetSchema: string | undefined): string => (
      propertyName && propertyName.trim() ? propertyName
        : (targetSchema && targetSchema.trim() ? targetSchema : 'related')
    );

    const relationLabel = (propertyName: string, targetSchema: string): string => {
      const name = safeName(propertyName, targetSchema);
      const key = `customStats.relations.${name}.label`;
      const translated = t(key);
      if (translated !== key) return translated;
      // Fallback : si name === targetSchema, n'affiche pas "(targetSchema)" en doublon
      return name === targetSchema ? name : `${name} (${targetSchema})`;
    };

    const relationSublabel = (propertyName: string, targetSchema: string, relType: string): string => {
      const name = safeName(propertyName, targetSchema);
      const key = `customStats.relations.${name}.sublabel`;
      const translated = t(key);
      if (translated !== key) return translated;
      // Fallback générique
      const dirKey = relType === 'one-to-many'
        ? 'customStats.relations.genericOneToMany'
        : 'customStats.relations.genericManyToOne';
      return t(dirKey, { target: targetSchema || name });
    };

    const availableRelations = computed(() => {
      const currentSource = availableSources.value.find(
        (s) => s.type === form.source_type && s.schemaName === form.source_schema_name,
      );
      const rels = currentSource?.relationships ?? [];
      return rels
        // Skip relationships malformées (sans target identifiable)
        .filter((r) => r.propertyName || r.targetSchema)
        .map((r) => {
          const name = safeName(r.propertyName, r.targetSchema);
          return {
            name,
            icon: RELATION_ICONS[name] ?? RELATION_ICONS[r.targetSchema] ?? 'far fa-link',
            label: relationLabel(r.propertyName, r.targetSchema),
            sublabel: relationSublabel(r.propertyName, r.targetSchema, r.type),
            cardinality: r.type === 'one-to-many' ? 'many' : 'one',
          };
        });
    });

    // True dès qu'au moins une relation one-to-many est cochée — sert à
    // afficher un bandeau explicatif sur les étapes Mesures / Dimensions.
    const hasOneToManyRelationSelected = computed(() =>
      availableRelations.value.some(
        (r) => r.cardinality === 'many' && (form.relations_chain ?? []).includes(r.name),
      ),
    );

    // ----- Aggregation cards (par mesure) -----
    const aggregationCardsForMeasure = (measure: KpiMeasure) => ALL_AGGREGATION_TYPES.map((value) => {
      const meta = AGGREGATION_META[value];
      const compatible = !measure.field
        || (AGGREGATIONS_BY_FIELD_TYPE[
          availableFields.value.find((f) => f.path === measure.field)?.type ?? ''
        ] ?? []).includes(value);
      return {
        value,
        icon: meta.icon,
        label: t(meta.labelKey),
        sublabel: t(meta.sublabelKey),
        compatible,
      };
    });

    // Les champs issus d'une relation one-to-many (relationCardinality='many')
    // ne peuvent pas servir de mesure / dimension / time_field car cela
    // dupliquerait les rows main. Ils restent utilisables comme **filtre**
    // uniquement (le backend les transforme en EXISTS).
    const isOneToManyField = (f: AvailableField) =>
      f.relationCardinality === 'many';

    // ----- Field options (par mesure : filtre sur le type d'agrégation choisi) -----
    const aggregationFieldOptionsForMeasure = (measure: KpiMeasure) => availableFields.value.filter((f) => {
      if (isOneToManyField(f)) return false;
      const compat = AGGREGATIONS_BY_FIELD_TYPE[f.type] ?? [];
      return compat.includes(measure.type);
    });

    const dimensionFieldOptions = computed(() => availableFields.value.filter(
      (f) => !isOneToManyField(f) && DIMENSION_ELIGIBLE_TYPES.includes(f.type),
    ));

    const timeFieldOptions = computed(() => availableFields.value.filter(
      (f) => !isOneToManyField(f) && TIME_FIELD_ELIGIBLE_TYPES.includes(f.type),
    ));

    // ----- Helpers -----
    const aggregationLabel = (agg: AggregationType): string => {
      const meta = AGGREGATION_META[agg];
      return meta ? t(meta.labelKey) : agg;
    };

    const typeLabel = (type: string): string => t(`customStats.fieldType.${type}`, type);

    /**
     * Display label with relation prefix when the field comes from a joined
     * source. Example: "Pays" → "Pays (du contact)" when contact_country
     * est fetché via la relation `contacts`.
     */
    const displayLabel = (field: AvailableField): string => {
      if (field.source !== 'relation' || !field.relationPath?.length) {
        return field.label;
      }
      const chain = field.relationPath.join(' › ');
      return `${field.label} (${chain})`;
    };

    // Hints métier pour les champs avec une sémantique non-évidente
    // (calculés, expressions, libellés normalisés…). Affichés en tooltip
    // sur les options des dropdowns mesure / dim / time.
    // Clé = path complet (ex: 't.amount_eur') ou colonne nue (ex: 'amount_eur').
    const FIELD_HINTS: Record<string, { fr: string; en: string }> = {
      amount_eur: {
        fr: 'Converti en EUR (amount × currency_rate_to_euro). Utilisez ce champ pour cumuler des shops multi-devises.',
        en: 'Converted to EUR (amount × currency_rate_to_euro). Use this field to aggregate multi-currency shops.',
      },
      stock_value: {
        fr: 'Calculé : prix × quantité. Somme = valeur totale du stock.',
        en: 'Computed: price × quantity. Sum gives total stock value.',
      },
      discount_pct: {
        fr: 'Pourcentage de remise effectif sur le prix promo. Moyenne sur le catalogue donne la remise moyenne.',
        en: 'Effective discount percentage on the promo price. Average across the catalog gives mean discount.',
      },
      line_total: {
        fr: 'Calculé : prix unitaire × quantité de la ligne. Somme par produit/marque/catégorie = CA correspondant.',
        en: 'Computed: unit price × line quantity. Sum per product/brand/category = corresponding revenue.',
      },
      discount_ratio: {
        fr: 'Pourcentage de remise sur la commande (voucher_amount / amount × 100). Moyenne sur les commandes utilisant un voucher.',
        en: 'Discount percentage on the order (voucher_amount / amount × 100). Average across orders using a voucher.',
      },
      cart_to_order_minutes: {
        fr: 'Délai en minutes entre la date du panier et la commande. Moyenne mesure la friction d\'achat.',
        en: 'Time in minutes between cart and order date. Average measures purchase friction.',
      },
      order_hour: {
        fr: 'Heure (0-23) de la commande. Idéal en dimension pour identifier les pics d\'activité.',
        en: 'Hour (0-23) of the order. Ideal as a dimension to identify activity spikes.',
      },
      order_dow: {
        fr: 'Jour de la semaine (Lundi, Mardi…). Dimension pour identifier les jours forts.',
        en: 'Day of week (Monday, Tuesday…). Dimension to identify peak days.',
      },
      amount_bucket: {
        fr: 'Tranches de montant : < 50€, 50-100€, 100-200€, 200-500€, > 500€.',
        en: 'Amount buckets: < €50, €50-100, €100-200, €200-500, > €500.',
      },
      cart_status: {
        fr: 'Converti / Abandonné, basé sur is_valid. Dimension clé pour le taux d\'abandon.',
        en: 'Converted / Abandoned, based on is_valid. Key dimension for abandon rate.',
      },
      in_stock_label: {
        fr: 'Oui (quantity > 0) / Non (quantity = 0). Dimension simple pour la disponibilité.',
        en: 'Yes (quantity > 0) / No (quantity = 0). Simple dimension for availability.',
      },
      stock_level: {
        fr: 'Rupture / Bas / Moyen / Élevé. Tranches de niveau de stock.',
        en: 'Out / Low / Medium / High. Stock level buckets.',
      },
      has_discount_label: {
        fr: 'En promo / Plein tarif, basé sur la présence d\'un price_discount inférieur au price.',
        en: 'On sale / Full price, based on price_discount being lower than price.',
      },
      has_voucher_label: {
        fr: 'Oui / Non — la commande contient-elle un code promo.',
        en: 'Yes / No — does the order contain a voucher.',
      },
      spm_voucher_used_label: {
        fr: 'SPM / Externe — origine du code promo utilisé.',
        en: 'SPM / External — source of the voucher used.',
      },
      is_valid_label: {
        fr: 'Validée / Annulée — état final de la commande.',
        en: 'Validated / Cancelled — final order state.',
      },
      active_label: {
        fr: 'Actif / Inactif — état actuel.',
        en: 'Active / Inactive — current state.',
      },
      category_active_label: {
        fr: 'Active / Inactive — état de la catégorie.',
        en: 'Active / Inactive — category state.',
      },
      category_level_label: {
        fr: 'Racine / Sous-catégorie — niveau dans l\'arborescence.',
        en: 'Root / Sub-category — level in the tree.',
      },
      manufacturer_active_label: {
        fr: 'Active / Inactive — état de la marque.',
        en: 'Active / Inactive — brand state.',
      },
      // Voucher fields
      type_voucher_label: {
        fr: 'Réduction fixe / Pourcentage / Frais de port offerts — type de code émis.',
        en: 'Fixed amount / Percentage / Free shipping — type of voucher issued.',
      },
      validity_label: {
        fr: 'À venir / Actif / Expiré — basé sur date_from et date_to comparés à NOW().',
        en: 'Upcoming / Active / Expired — based on date_from and date_to vs NOW().',
      },
      used_label: {
        fr: 'Utilisé / Non utilisé — le code a-t-il été consommé. Ratio = taux d\'utilisation.',
        en: 'Used / Unused — has the voucher been consumed. Ratio = usage rate.',
      },
      origin_label: {
        fr: 'ShopiMind / Boutique — un code est ShopiMind si son code commence par « SPM_ », sinon il provient de la boutique.',
        en: 'ShopiMind / Shop — a code is ShopiMind-issued if it starts with "SPM_", otherwise it comes from the shop.',
      },
      // Group fields
      group_active_label: {
        fr: 'Actif / Inactif — état du groupe client.',
        en: 'Active / Inactive — customer group state.',
      },
      group_name: {
        fr: 'Nom du groupe principal du contact (appartenance la plus récente). Idéal en dimension pour CA par groupe.',
        en: 'Main group name of the contact (most recent membership). Ideal as dimension for revenue per group.',
      },
      // Variant fields
      combination_name: {
        fr: 'Nom de la déclinaison (ex: « Rouge - Taille L »). Idéal en dimension pour ventiler par variante.',
        en: 'Variant name (ex: "Red - Size L"). Ideal as dimension to break down by variant.',
      },
      default_label: {
        fr: 'Par défaut / Alternative — la variante est-elle celle proposée par défaut au catalogue.',
        en: 'Default / Alternative — is this the variant shown by default in the catalog.',
      },
      // Order status
      status_name: {
        fr: 'Libellé humain du statut, configuré par le marchand (ex: « Expédiée », « En attente de paiement »).',
        en: 'Human-readable status label, configured by the merchant (ex: "Shipped", "Awaiting payment").',
      },
    };

    const fieldHint = (field: AvailableField): string => {
      const hint = FIELD_HINTS[field.column];
      const lang = locale.value === 'en' ? 'en' : 'fr';
      const sqlPath = field.path;
      if (hint) {
        return `${sqlPath}\n\n${hint[lang]}`;
      }
      return sqlPath;
    };

    // Formate un nombre avec séparateurs de milliers selon la locale.
    const formatNumber = (n: number): string => {
      if (!Number.isFinite(n)) return String(n);
      try {
        return new Intl.NumberFormat(locale.value).format(n);
      } catch {
        return String(n);
      }
    };

    // Projection annualisée du nb de rows scannées : `scanned × (365 / previewDays)`.
    const estimatedYearlyRows = (preview: PreviewResult): number => {
      if (!preview.sourceRowsScanned || !preview.previewDays) return 0;
      return Math.round(preview.sourceRowsScanned * (365 / preview.previewDays));
    };

    const filterOperatorOptions = [
      { value: 'eq', label: t('customStats.filterOp.eq') },
      { value: 'neq', label: t('customStats.filterOp.neq') },
      { value: 'gt', label: t('customStats.filterOp.gt') },
      { value: 'gte', label: t('customStats.filterOp.gte') },
      { value: 'lt', label: t('customStats.filterOp.lt') },
      { value: 'lte', label: t('customStats.filterOp.lte') },
      { value: 'in', label: t('customStats.filterOp.in') },
      { value: 'not_in', label: t('customStats.filterOp.not_in') },
      { value: 'contains', label: t('customStats.filterOp.contains') },
      { value: 'starts_with', label: t('customStats.filterOp.starts_with') },
      { value: 'ends_with', label: t('customStats.filterOp.ends_with') },
      { value: 'is_null', label: t('customStats.filterOp.is_null') },
      { value: 'is_not_null', label: t('customStats.filterOp.is_not_null') },
    ];

    // ----- Step navigation -----
    const isMeasureValid = (m: KpiMeasure): boolean => {
      const labelOk = shopLangs.some((l) => (m.label?.[l] ?? '').trim().length > 0);
      if (!labelOk) return false;
      if (m.type === 'count') return true;
      return !!m.field;
    };

    const isDimensionValid = (d: KpiDimension): boolean => {
      const labelOk = shopLangs.some((l) => (d.label?.[l] ?? '').trim().length > 0);
      return labelOk && !!d.field;
    };

    const canGoNext = computed(() => {
      const step = currentStep.value;
      if (step === 'source') return !!form.source_schema_name && form.source_schema_name !== '__custom_aggregator__';
      if (step === 'measures') return form.measures.length > 0 && form.measures.every(isMeasureValid);
      if (step === 'dimensions') return (form.dimensions ?? []).every(isDimensionValid);
      if (step === 'time') return !timeEnabled.value || !!form.time_field;
      return true; // relations / filters / display
    });

    const canSave = computed(() => {
      const hasName = shopLangs.some((l) => (nameByLang[l] ?? '').trim().length > 0);
      return hasName
        && !!form.source_schema_name
        && form.measures.length > 0
        && form.measures.every(isMeasureValid)
        && (form.dimensions ?? []).every(isDimensionValid);
    });

    const canRunPreview = computed(() => !!form.source_schema_name && form.measures.length > 0 && !isEdit.value);

    // ----- Modal title -----
    const modalTitle = computed(() => (isEdit.value
      ? t('customStats.editor.editTitle')
      : t('customStats.editor.createTitle')));
    const modalSubtitle = computed(() => (isEdit.value ? t('customStats.editor.editLockNotice') : ''));

    // ----- Summary lines (live preview) -----
    const summarySource = computed(() => {
      if (!form.source_schema_name) return '';
      const meta = SYSTEM_SOURCE_META[form.source_schema_name];
      return meta ? t(meta.labelKey) : form.source_schema_name;
    });
    const summaryRelations = computed(() => t('customStats.editor.preview.line.relations', {
      list: (form.relations_chain ?? []).join(', '),
    }));
    const fieldLabelByPath = (path?: string): string => {
      if (!path) return '';
      const f = availableFields.value.find((x) => x.path === path);
      return f ? displayLabel(f) : (path.split('.').pop() ?? '');
    };

    const summaryMeasures = computed(() => form.measures.map((m) => {
      const label = m.label?.[locale.value] || m.label?.fr || m.label?.en || '';
      const aggLabel = aggregationLabel(m.type);
      const fieldLbl = m.field ? ` (${fieldLabelByPath(m.field)})` : '';
      return label ? `${label} : ${aggLabel}${fieldLbl}` : `${aggLabel}${fieldLbl}`;
    }));
    const summaryDimensions = computed(() => {
      const dims = form.dimensions ?? [];
      const labels = dims
        .map((d) => d.label?.[locale.value] || d.label?.fr || d.label?.en || fieldLabelByPath(d.field))
        .filter(Boolean);
      return t('customStats.editor.preview.line.dimensions', {
        list: labels.join(', '),
      });
    });
    const summaryTime = computed(() => {
      const fl = fieldLabelByPath(form.time_field) || form.time_field;
      return t('customStats.editor.preview.line.time', { field: fl });
    });
    const summaryFilters = computed(() => t('customStats.editor.preview.line.filters', {
      count: form.filters?.length ?? 0,
    }));

    // ----- Lifecycle -----
    onMounted(async () => {
      try {
        availableSources.value = await fetchAvailableSources();
      } catch (err: any) {
        toast.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to load sources', life: 5000 });
      }

      if (editId.value) await loadExistingKpi(editId.value, false);
      else if (duplicateId.value) await loadExistingKpi(duplicateId.value, true);
    });

    async function loadFields() {
      if (!form.source_schema_name) {
        availableFields.value = [];
        return;
      }
      isLoadingFields.value = true;
      try {
        availableFields.value = await fetchAvailableFields(
          form.source_type,
          form.source_schema_name,
          form.relations_chain ?? [],
        );
      } catch (err: any) {
        toast.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to load fields', life: 5000 });
        availableFields.value = [];
      } finally {
        isLoadingFields.value = false;
      }
    }

    async function loadExistingKpi(id: string, asDuplicate: boolean) {
      try {
        const kpi = await fetchKpi(id);
        fillFormFromKpi(kpi);
        if (asDuplicate) {
          form.lookback_max_months = DEFAULT_LOOKBACK_MONTHS;
          shopLangs.forEach((l) => {
            if (nameByLang[l]) nameByLang[l] = `${nameByLang[l]} (copy)`;
          });
        }
        await loadFields();
      } catch (err: any) {
        toast.add({ severity: 'error', summary: 'Error', detail: err?.message ?? 'Failed to load KPI', life: 5000 });
      }
    }

    function fillFormFromKpi(kpi: CustomKpiDefinition) {
      form.source_type = kpi.source_type;
      form.source_schema_name = kpi.source_schema_name;
      form.relations_chain = kpi.relations_chain ?? [];
      form.time_field = kpi.time_field ?? '';
      form.time_field_explicit = kpi.time_field_explicit ?? true;
      form.filters = (kpi.filters_json ?? []).map((f) => ({ ...f }));
      form.measures = (kpi.measures ?? []).map((m) => ({
        id_measure: m.id_measure,
        type: m.type,
        field: m.field,
        format: m.format ?? 'number',
        suffix: m.suffix ?? null,
        label: { ...(m.label ?? {}) },
        position: m.position,
      }));
      if (form.measures.length === 0) form.measures = [makeDefaultMeasure()];
      form.dimensions = (kpi.dimensions ?? []).map((d) => ({
        id_dimension: d.id_dimension,
        field: d.field,
        label: { ...(d.label ?? {}) },
        position: d.position,
      }));
      form.lookback_max_months = kpi.lookback_max_months;

      timeEnabled.value = (kpi.time_field_explicit ?? true) && !!kpi.time_field;

      Object.entries(kpi.name ?? {}).forEach(([l, v]) => { nameByLang[l] = v; });
      Object.entries(kpi.description ?? {}).forEach(([l, v]) => { descByLang[l] = v; });

      (form.filters ?? []).forEach((f, i) => {
        if (f.value !== undefined) {
          filterValueProxy[i] = Array.isArray(f.value) ? f.value.join(',') : String(f.value);
        }
      });
    }

    // ----- Step actions -----
    function isSourceSelected(card: any): boolean {
      if (card.schemaName === '__custom_aggregator__') {
        return form.source_type === 'custom' && customDataSources.value.some((s) => s.schemaName === form.source_schema_name);
      }
      return form.source_type === card.type && form.source_schema_name === card.schemaName;
    }

    function selectSource(card: any) {
      if (isEdit.value) return;
      if (card.schemaName === '__custom_aggregator__') {
        // No-op — l'utilisateur doit cliquer sur une sous-carte
        return;
      }
      form.source_type = card.type;
      form.source_schema_name = card.schemaName;
      form.relations_chain = [];
      form.measures = [makeDefaultMeasure()];
      form.dimensions = [];
      form.time_field = '';
      form.time_field_explicit = true;
      form.filters = [];
      timeEnabled.value = false;
      previewResult.value = null;
      loadFields();
    }

    function isRelationSelected(name: string): boolean {
      return (form.relations_chain ?? []).includes(name);
    }

    function toggleRelation(name: string) {
      if (isEdit.value) return;
      const chain = form.relations_chain ?? [];
      if (chain.includes(name)) {
        form.relations_chain = chain.filter((r) => r !== name);
      } else {
        form.relations_chain = [...chain, name];
      }
      loadFields();
    }

    function addMeasure() {
      if (form.measures.length >= MAX_MEASURES_PER_KPI) return;
      form.measures.push(makeDefaultMeasure());
    }

    function removeMeasure(idx: number) {
      if (form.measures.length <= 1) return; // toujours au moins 1
      form.measures.splice(idx, 1);
    }

    function setMeasureType(idx: number, value: AggregationType) {
      const m = form.measures[idx];
      if (!m) return;
      m.type = value;
      if (value === 'count') m.field = undefined;
      else {
        // Si le field actuel n'est plus compatible, on le remet à zéro
        const f = availableFields.value.find((x) => x.path === m.field);
        const compat = f ? AGGREGATIONS_BY_FIELD_TYPE[f.type] ?? [] : [];
        if (m.field && !compat.includes(value)) m.field = undefined;
      }
    }

    function setMeasureField(idx: number, path: string | undefined) {
      const m = form.measures[idx];
      if (!m) return;
      m.field = path;
    }

    function setMeasureLabel(idx: number, lang: string, value: string) {
      const m = form.measures[idx];
      if (!m) return;
      if (!m.label) m.label = {};
      m.label[lang] = value;
    }

    function setMeasureLabelObject(idx: number, value: Record<string, string> | null | undefined) {
      const m = form.measures[idx];
      if (!m) return;
      m.label = { ...(value ?? {}) };
    }

    function setMeasureFormat(
      idx: number,
      value: 'number' | 'currency' | 'percent' | 'integer',
    ) {
      const m = form.measures[idx];
      if (!m) return;
      m.format = value;
    }

    function setMeasureSuffix(idx: number, value: string) {
      const m = form.measures[idx];
      if (!m) return;
      const trimmed = (value ?? '').slice(0, 8).trim();
      m.suffix = trimmed.length > 0 ? trimmed : null;
    }

    const measureFormatOptions = computed(() => [
      {
        value: 'number',
        label: t('customStats.editor.step.measures.formatOptions.number'),
      },
      {
        value: 'currency',
        label: t('customStats.editor.step.measures.formatOptions.currency'),
      },
      {
        value: 'percent',
        label: t('customStats.editor.step.measures.formatOptions.percent'),
      },
      {
        value: 'integer',
        label: t('customStats.editor.step.measures.formatOptions.integer'),
      },
    ]);

    // ---- Dimensions handlers ----
    function addDimension() {
      if (!form.dimensions) form.dimensions = [];
      if (form.dimensions.length >= MAX_DIMENSIONS_PER_KPI) return;
      form.dimensions.push(makeDefaultDimension());
    }

    function removeDimension(idx: number) {
      form.dimensions?.splice(idx, 1);
    }

    function setDimensionField(idx: number, value: string) {
      const d = form.dimensions?.[idx];
      if (!d) return;
      d.field = value;
    }

    function setDimensionLabel(idx: number, lang: string, value: string) {
      const d = form.dimensions?.[idx];
      if (!d) return;
      if (!d.label) d.label = {};
      d.label[lang] = value;
    }

    function setDimensionLabelObject(idx: number, value: Record<string, string> | null | undefined) {
      const d = form.dimensions?.[idx];
      if (!d) return;
      d.label = { ...(value ?? {}) };
    }

    function setTime(enabled: boolean) {
      if (isEdit.value) return;
      timeEnabled.value = enabled;
      form.time_field_explicit = enabled;
      if (!enabled) {
        // Fallback automatique : 1er time field disponible.
        const fallback = timeFieldOptions.value[0]?.path ?? '';
        form.time_field = fallback;
      }
    }

    function addFilter() {
      if ((form.filters?.length ?? 0) >= MAX_FILTERS) return;
      if (!form.filters) form.filters = [];
      form.filters.push({ field: '', operator: 'eq', value: '' });
    }

    function removeFilter(idx: number) {
      form.filters?.splice(idx, 1);
      delete filterValueProxy[idx];
    }

    function filterNeedsValue(op: string): boolean {
      return op !== 'is_null' && op !== 'is_not_null';
    }

    function onFilterValueChange(idx: number, val: string) {
      filterValueProxy[idx] = val;
      if (!form.filters || !form.filters[idx]) return;
      const op = form.filters[idx].operator;
      if (op === 'in' || op === 'not_in') {
        form.filters[idx].value = val.split(',').map((v) => v.trim()).filter(Boolean);
      } else {
        form.filters[idx].value = val;
      }
    }

    function goNextStep() {
      if (currentStepIndex.value < visibleSteps.value.length - 1) {
        currentStepIndex.value += 1;
      }
    }

    function goBackStep() {
      if (currentStepIndex.value > 0) {
        currentStepIndex.value -= 1;
      }
    }

    function buildPayload(): CreateCustomKpiPayload {
      const name: Record<string, string> = {};
      Object.entries(nameByLang).forEach(([l, v]) => { if (v?.trim()) name[l] = v.trim(); });
      const description: Record<string, string> = {};
      Object.entries(descByLang).forEach(([l, v]) => { if (v?.trim()) description[l] = v.trim(); });

      const measures: KpiMeasure[] = form.measures.map((m, i) => {
        const cleanLabel: Record<string, string> = {};
        Object.entries(m.label ?? {}).forEach(([l, v]) => {
          if (v?.trim()) cleanLabel[l] = v.trim();
        });
        return {
          ...(m.id_measure ? { id_measure: m.id_measure } : {}),
          type: m.type,
          field: m.field || undefined,
          format: m.format ?? 'number',
          suffix: m.suffix && m.suffix.length > 0 ? m.suffix : undefined,
          label: cleanLabel,
          position: m.position ?? i,
        };
      });

      const dimensions: KpiDimension[] = (form.dimensions ?? []).map((d, i) => {
        const cleanLabel: Record<string, string> = {};
        Object.entries(d.label ?? {}).forEach(([l, v]) => {
          if (v?.trim()) cleanLabel[l] = v.trim();
        });
        return {
          ...(d.id_dimension ? { id_dimension: d.id_dimension } : {}),
          field: d.field,
          label: cleanLabel,
          position: d.position ?? i,
        };
      });

      return {
        ...form,
        name,
        description: Object.keys(description).length ? description : undefined,
        measures,
        dimensions,
        time_field: form.time_field,
        time_field_explicit: form.time_field_explicit ?? true,
      };
    }

    async function onPreview() {
      isPreviewLoading.value = true;
      previewResult.value = null;
      try {
        const payload = { ...buildPayload(), preview_days: 7 };
        previewResult.value = await previewKpi(payload);
      } catch (err: any) {
        toast.add({ severity: 'error', summary: t('customStats.editor.toast.previewFailed'), detail: err?.message ?? 'Unknown', life: 8000 });
      } finally {
        isPreviewLoading.value = false;
      }
    }

    async function onSave() {
      isSaving.value = true;
      try {
        if (isEdit.value && editId.value) {
          const built = buildPayload();
          const updatePayload = {
            name: built.name,
            description: built.description,
            measures: built.measures,
            dimensions: built.dimensions,
            filters: form.filters,
          };
          await updateKpi(editId.value, updatePayload);
          toast.add({ severity: 'success', summary: 'OK', detail: t('customStats.editor.toast.updated'), life: 3000 });
        } else {
          await createKpi(buildPayload());
          toast.add({ severity: 'success', summary: 'OK', detail: t('customStats.editor.toast.created'), life: 3000 });
        }
        router.push({ name: 'custom-kpis.list' });
      } catch (err: any) {
        toast.add({ severity: 'error', summary: t('customStats.editor.toast.saveFailed'), detail: err?.message ?? 'Unknown', life: 8000 });
      } finally {
        isSaving.value = false;
      }
    }

    function goBack() {
      router.push({ name: 'custom-kpis.list' });
    }

    // ===== Templates de KPIs prêts à cloner =====
    const templatesModalOpen = ref(false);

    const templateGroups = computed(() => {
      const buckets: Record<string, CustomKpiTemplate[]> = {};
      for (const tpl of CUSTOM_KPI_TEMPLATES) {
        if (!buckets[tpl.group]) buckets[tpl.group] = [];
        buckets[tpl.group].push(tpl);
      }
      return CUSTOM_KPI_TEMPLATE_GROUP_ORDER
        .filter((key) => buckets[key]?.length)
        .map((key) => ({
          key,
          label: t(`customStats.templates.groups.${key}`),
          templates: buckets[key],
        }));
    });

    function openTemplatesModal() {
      templatesModalOpen.value = true;
    }

    async function applyTemplate(tpl: CustomKpiTemplate) {
      // On replace le form en partant du template (le name reste à la
      // discrétion du marchand). Toutes les listes sont clonées pour
      // éviter qu'éditer le form ne mute le template d'origine.
      const p = tpl.payload;
      form.source_type = p.source_type;
      form.source_schema_name = p.source_schema_name;
      form.relations_chain = [...(p.relations_chain ?? [])];
      form.measures = (p.measures ?? []).map((m) => ({
        ...m,
        label: { ...m.label },
      }));
      form.dimensions = (p.dimensions ?? []).map((d) => ({
        ...d,
        label: { ...d.label },
      }));
      form.time_field = p.time_field;
      form.time_field_explicit = p.time_field_explicit ?? true;
      form.filters = (p.filters ?? []).map((f) => ({ ...f }));
      timeEnabled.value = !!form.time_field;
      previewResult.value = null;
      templatesModalOpen.value = false;
      // Charge les fields de la nouvelle source pour que le wizard puisse
      // afficher / valider les paths référencés par le template.
      await loadFields();
    }

    return {
      t,
      MAX_FILTERS,
      MAX_LOOKBACK_MONTHS,
      MAX_MEASURES_PER_KPI,
      MAX_DIMENSIONS_PER_KPI,
      shopLangs,
      // form & state
      form,
      nameByLang,
      descByLang,
      filterValueProxy,
      timeEnabled,
      availableFields,
      isEdit,
      isSaving,
      // step
      currentStep,
      currentStepNumber,
      visibleSteps,
      isLastStep,
      canGoBack,
      canGoNext,
      progressPercent,
      canSave,
      canRunPreview,
      // titles
      modalTitle,
      modalSubtitle,
      // cards
      sourceCards,
      sourceGroups,
      hasOneToManyRelationSelected,
      // templates
      templatesModalOpen,
      templateGroups,
      openTemplatesModal,
      applyTemplate,
      // tooltips
      fieldHint,
      // preview cost
      formatNumber,
      estimatedYearlyRows,
      customDataSources,
      availableRelations,
      aggregationCardsForMeasure,
      aggregationFieldOptionsForMeasure,
      dimensionFieldOptions,
      timeFieldOptions,
      filterOperatorOptions,
      // helpers
      typeLabel,
      displayLabel,
      aggregationLabel,
      isSourceSelected,
      isRelationSelected,
      filterNeedsValue,
      // summary
      summarySource,
      summaryRelations,
      summaryMeasures,
      summaryDimensions,
      summaryTime,
      summaryFilters,
      // preview
      isPreviewLoading,
      previewResult,
      onPreview,
      // step actions
      selectSource,
      toggleRelation,
      addMeasure,
      removeMeasure,
      setMeasureType,
      setMeasureField,
      setMeasureLabel,
      setMeasureLabelObject,
      setMeasureFormat,
      setMeasureSuffix,
      measureFormatOptions,
      addDimension,
      removeDimension,
      setDimensionField,
      setDimensionLabel,
      setDimensionLabelObject,
      setTime,
      addFilter,
      removeFilter,
      onFilterValueChange,
      goNextStep,
      goBackStep,
      onSave,
      goBack,
    };
  },
});
</script>

<style lang="scss" scoped>
$primary: #10b981;
$primary-light: #d1fae5;
$primary-dark: #047857;
$accent: #6366f1;

.kpi-wizard-layout {
  display: flex;
  height: 100%;
  background: #f9fafb;
}

.kpi-wizard-form {
  flex: 1 1 60%;
  padding: 2rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.kpi-wizard-preview {
  flex: 0 0 38%;
  border-left: 1px solid #e5e7eb;
  padding: 2rem 1.5rem;
  background: white;
  overflow-y: auto;
}

/* Progress bar */
.kpi-progress-bar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}
.kpi-progress-track {
  flex: 1;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  overflow: hidden;
}
.kpi-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, $primary, $primary-dark);
  transition: width 0.3s ease;
}
.kpi-progress-label {
  font-size: 0.85rem;
  color: #6b7280;
  font-weight: 500;
  flex-shrink: 0;
}

/* Step section */
.kpi-step {
  flex: 1;
}
.kpi-step-title {
  margin: 0 0 0.5rem 0;
  font-size: 1.4rem;
  font-weight: 600;
  color: #111827;
}
.kpi-step-help {
  color: #6b7280;
  margin: 0 0 1.5rem 0;
  font-size: 0.95rem;
}
.kpi-field-label {
  display: block;
  font-weight: 500;
  font-size: 0.9rem;
  color: #374151;
  margin-bottom: 0.5rem;
}

/* Templates shortcut & picker */
.kpi-templates-shortcut {
  margin: 0.5rem 0 1.25rem;
}
.kpi-templates-button {
  background: linear-gradient(90deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #a7f3d0;
  color: #047857;
  padding: 0.55rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s;
}
.kpi-templates-button:hover {
  background: linear-gradient(90deg, #d1fae5 0%, #a7f3d0 100%);
  border-color: #6ee7b7;
}
.kpi-templates-help {
  color: #6b7280;
  font-size: 0.9rem;
  margin: 0 0 1rem 0;
}
.kpi-templates-group {
  margin-bottom: 1.25rem;
}
.kpi-templates-group:last-child {
  margin-bottom: 0;
}
.kpi-templates-group-title {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 0 0 0.5rem 0;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid #e5e7eb;
}
.kpi-templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 0.75rem;
}
.kpi-template-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 0.85rem 1rem;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}
.kpi-template-card:hover {
  border-color: #047857;
  box-shadow: 0 2px 8px rgba(4, 120, 87, 0.12);
  transform: translateY(-1px);
}
.kpi-template-icon {
  font-size: 1.2rem;
  color: #047857;
}
.kpi-template-label {
  font-weight: 600;
  font-size: 0.92rem;
  color: #111827;
}
.kpi-template-desc {
  font-size: 0.78rem;
  color: #6b7280;
  line-height: 1.4;
}

/* Source groups */
.kpi-source-group {
  margin-bottom: 1.5rem;
}
.kpi-source-group:last-child {
  margin-bottom: 0;
}
.kpi-source-group-title {
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  margin: 0 0 0.6rem 0;
  padding-bottom: 0.3rem;
  border-bottom: 1px solid #e5e7eb;
}

/* Info banner — relation 1-vers-N filter-only */
.kpi-info-banner {
  margin-top: 0.75rem;
  padding: 0.75rem 1rem;
  background: #eff6ff;
  border-left: 3px solid #2563eb;
  border-radius: 0 6px 6px 0;
  color: #1e40af;
  font-size: 0.85rem;
  line-height: 1.45;
}

/* Badge "Filtre uniquement" sur les cartes de relations 1-vers-N */
.kpi-relation-badge {
  position: absolute;
  top: 0.4rem;
  left: 0.4rem;
  background: #fef3c7;
  color: #b45309;
  font-size: 0.65rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 6px;
  border-radius: 10px;
  border: 1px solid #fde68a;
}

/* Cards */
.kpi-card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}
.kpi-card {
  position: relative;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem 1rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  &:hover:not(:disabled):not(.kpi-card--selected) {
    border-color: $primary;
    background: #f0fdf4;
    transform: translateY(-1px);
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
  &--selected {
    border-color: $primary;
    background: $primary-light;
  }
  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
.kpi-card-icon {
  font-size: 1.75rem;
  color: #6b7280;

  &--accent { color: $accent; }
}
.kpi-card--selected .kpi-card-icon { color: $primary-dark; }
.kpi-card-label {
  font-weight: 600;
  font-size: 0.95rem;
  color: #111827;
}
.kpi-card-sublabel {
  font-size: 0.8rem;
  color: #6b7280;
  line-height: 1.3;
}
.kpi-card-checkmark {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  color: $primary-dark;
  font-size: 1.1rem;
}

/* Mini cards (custom data) */
.kpi-mini-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.kpi-mini-card {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 999px;
  padding: 0.4rem 0.9rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.15s;
  &:hover:not(:disabled) {
    border-color: $primary;
    background: #f0fdf4;
  }
  &--selected {
    border-color: $primary;
    background: $primary-light;
    color: $primary-dark;
    font-weight: 600;
  }
  &--disabled { opacity: 0.6; cursor: not-allowed; }
}
.kpi-mini-icon { color: #6b7280; }

/* Toggle group */
.kpi-toggle-group {
  display: inline-flex;
  background: #f3f4f6;
  padding: 4px;
  border-radius: 8px;
  gap: 4px;
}
.kpi-toggle-btn {
  padding: 0.5rem 1.25rem;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.9rem;
  color: #6b7280;
  transition: all 0.15s;
  &:hover:not(:disabled) { color: #111827; }
  &--active {
    background: white;
    color: #111827;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
}

/* Chart type cards */
.kpi-chart-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 0.75rem;
}
.kpi-chart-card {
  position: relative;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  padding: 1rem 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;

  &:hover:not(:disabled):not(.kpi-chart-card--selected) {
    border-color: $primary;
    background: #f0fdf4;
  }
  &--selected {
    border-color: $primary;
    background: $primary-light;
  }
  &--disabled { opacity: 0.4; cursor: not-allowed; }
}
.kpi-chart-icon { font-size: 1.5rem; color: #6b7280; }
.kpi-chart-card--selected .kpi-chart-icon { color: $primary-dark; }
.kpi-chart-label { font-size: 0.85rem; font-weight: 500; }

/* Filters */
.kpi-filter-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.kpi-filter-field { flex: 2; }
.kpi-filter-op { flex: 0 0 180px; }
.kpi-filter-value { flex: 2; }

/* Lang rows */
.kpi-lang-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

/* Empty hint */
.kpi-empty-hint {
  background: #f9fafb;
  border: 1px dashed #d1d5db;
  border-radius: 8px;
  padding: 1.25rem;
  text-align: center;
  color: #6b7280;
  font-size: 0.9rem;
}

/* Step nav (footer) */
.kpi-step-nav {
  display: flex;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 2rem;
  border-top: 1px solid #e5e7eb;
}

/* Preview panel */
.kpi-preview-card {
  position: sticky;
  top: 0;
}
.kpi-preview-header {
  display: flex;
  align-items: center;
  font-weight: 600;
  font-size: 1rem;
  color: $primary-dark;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 1rem;
}
.kpi-preview-placeholder {
  color: #9ca3af;
  font-style: italic;
  text-align: center;
  padding: 2rem 1rem;
}
.kpi-preview-summary {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.kpi-preview-line {
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #374151;
  line-height: 1.4;
}
.kpi-preview-line-icon {
  color: $primary;
  flex-shrink: 0;
  margin-top: 0.2rem;
}
.kpi-preview-actions {
  margin-top: 1rem;
  text-align: center;
}
.kpi-preview-result {
  border-top: 1px dashed #e5e7eb;
  padding-top: 1rem;
}
.kpi-preview-result-header {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.kpi-preview-cost {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.6rem;
  margin: 0.6rem 0 0.8rem;
  padding: 0.7rem 0.8rem;
  background: linear-gradient(90deg, #ecfdf5 0%, #f0fdf4 100%);
  border: 1px solid #bbf7d0;
  border-radius: 6px;
}
.kpi-preview-cost-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.kpi-preview-cost-label {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #047857;
  font-weight: 600;
}
.kpi-preview-cost-value {
  font-size: 1.05rem;
  font-weight: 700;
  color: #064e3b;
  font-variant-numeric: tabular-nums;
}
.kpi-preview-cost-value--warn {
  color: #b45309;
}
.kpi-preview-cost-sub {
  font-size: 0.7rem;
  color: #6b7280;
}
.kpi-preview-table {
  font-size: 0.8rem;
}
.kpi-preview-sql {
  background: #f3f4f6;
  padding: 0.5rem;
  font-size: 0.7rem;
  border-radius: 4px;
  overflow-x: auto;
  max-height: 200px;
}

.space-y-2 > * + * { margin-top: 0.5rem; }
.space-y-3 > * + * { margin-top: 0.75rem; }

/* Measures step */
.kpi-measures-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.kpi-measure-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
.kpi-measure-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid #f3f4f6;
}
.kpi-measure-index {
  font-weight: 700;
  color: $primary-dark;
  background: $primary-light;
  border-radius: 999px;
  padding: 0.15rem 0.6rem;
  font-size: 0.85rem;
}
.kpi-measure-summary {
  flex: 1;
  font-weight: 500;
  color: #374151;
}
.kpi-measure-section + .kpi-measure-section {
  margin-top: 0.25rem;
}
</style>
